import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandCurrencyOptions,
  normalizeBrandCurrency,
  isBrandCurrencyLocked,
  SAUDI_CURRENCY_CODE,
} from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator, Search, X, Filter, FileText,
  ChevronDown, ChevronUp, LayoutGrid,
  CheckCircle, TrendingUp, Hash, BadgeCheck, Coins, Globe, Settings, Layers,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  countArticleActive,
  countArticleInactive,
  isArticleActiveStatus,
  matchesArticleStatusFilter,
  getArticleStatusLabel,
} from './articlesStatusUtils';
import {
  buildArticleStatusBadgeOptions,
  buildArticleStatusColumnFilterOptions,
  buildArticleStatusSelectOptions,
  getCatalogStatusBadgeStyle,
} from './articleCatalogStatusOptions';
import {buildArticlesImportExportProps,
  buildBulkDeleteFilterAction,
  createArticleStatusChangeHandler,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd, ArticlesConfigNotice, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { ErpFieldLabel } from '../../shared/utils/erpFormFieldClasses';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';

const NS = 'modules.article.pages.costMethodsPage';
const API_BASE = '/api/articles/cost-methods';

const METHOD_TYPE_DEFS = [
  { value: 'fifo', labelKey: 'type_fifo' },
  { value: 'weighted_average', labelKey: 'type_weighted_average' },
  { value: 'standard', labelKey: 'type_standard' },
  { value: 'last_purchase', labelKey: 'type_last_purchase' },
];

const CURRENCY_DEFS = getBrandCurrencyOptions();

const EMPTY_FORM = {
  method_code: '',
  name: '',
  method_type: '',
  description: '',
  is_default: false,
  currency: getBrandDefaultCurrency(),
  status: 'actif',
  notes: '',
};

const CostMethodsPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const p = (key, options) => t(`${NS}.${key}`, options);
  const qawaem = isSaudiBrand();
  const articlesConfig = useArticlesConfig();
  const currencyLocked = isBrandCurrencyLocked();
  const currencySuffix = getBrandCurrencySuffix();
  const fmtCurrency = (code) => normalizeBrandCurrency(code) || p('empty_dash');

  const methodTypeOptions = useMemo(
    () => METHOD_TYPE_DEFS.map(({ value, labelKey }) => ({
      value,
      label: p(labelKey),
    })),
    [t],
  );

  const statusOptions = useMemo(() => buildArticleStatusSelectOptions(t), [t]);

  const STATUS_OPTIONS = useMemo(() => buildArticleStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildArticleStatusColumnFilterOptions(t), [t]);

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler(API_BASE, () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  const currencyOptions = useMemo(
    () => CURRENCY_DEFS.map(({ value, label }) => ({ value, label })),
    [],
  );

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [statusFilter, setStatusFilter] = useState('all');
  const [mainViewMode, setMainViewMode] = useState('all_methods');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'طريقة التكلفة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'طريقة تكلفة' });

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    loadMethods();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMethods = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get(API_BASE);
      const data = response?.data ?? response;
      setMethods(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading cost methods:', error);
      setMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const getMethodTypeLabel = (type) => {
    const def = METHOD_TYPE_DEFS.find((item) => item.value === type);
    return def ? p(def.labelKey) : type || p('empty_dash');
  };

  const generateMethodCode = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'costMethod', 'CM');
    return generateEntityReference(prefix, methods, (m) => m.method_code);
  }, [methods, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.method_type || !formData.status) {
      window.alert(p('alert_required'));
      return;
    }

    try {
      setLoading(true);
      const methodCode = (formData.method_code || '').trim()
        || (articlesConfig.autoGenerateCostMethodReference !== false ? generateMethodCode() : '');
      const payload = {
        method_code: methodCode,
        name: formData.name.trim(),
        method_type: formData.method_type,
        description: formData.description || null,
        is_default: formData.is_default ? 1 : 0,
        currency: normalizeBrandCurrency(formData.currency) || null,
        status: formData.status,
        notes: formData.notes || null,
      };

      if (editingRecord) {
        await localApiAdapter.put(`${API_BASE}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, entityLabel);
      }

      resetForm();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving cost method:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      method_code: record.method_code || '',
      name: record.name || '',
      method_type: record.method_type || '',
      description: record.description || '',
      is_default: record.is_default === 1 || record.is_default === true,
      currency: normalizeBrandCurrency(record.currency || ''),
      status: record.status || 'actif',
      notes: record.notes || '',
    });
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`${API_BASE}/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        const deleteCount = ids.length;
        await Promise.all(
          selectedRecords.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)),
        );
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting cost method:', error);
        notifyError(showNotify, p('alert_save_error'));
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setShowModal(false);
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(
      selectedRecords.includes(id)
        ? selectedRecords.filter((rId) => rId !== id)
        : [...selectedRecords, id],
    );
  };

  

  const postImportedRow = async (item) => {
    const payload = {
      name: String(item.name || '').trim(),
      method_type: item.method_type,
      description: item.description || null,
      is_default: item.is_default === true || item.is_default === 1 || item.is_default === '1' ? 1 : 0,
      currency: normalizeBrandCurrency(item.currency) || null,
      status: item.status,
      notes: item.notes || null,
    };
    if (!payload.name || !payload.method_type || !payload.status) return false;
    await localApiAdapter.post(API_BASE, payload);
    return true;
  };

  const handleImportCSV = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        if (!Array.isArray(imported)) {
          window.alert(p('import_invalid_format'));
          return;
        }
        let count = 0;
        for (const item of imported) {
          if (await postImportedRow(item)) count += 1;
        }
        setRefreshTrigger((prev) => prev + 1);
        setShowImportDropdown(false);
        if (count > 0) window.alert(p('import_success', { count }));
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('import_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        const rows = Array.isArray(imported) ? imported : [imported];
        let count = 0;
        for (const item of rows) {
          if (await postImportedRow(item)) count += 1;
        }
        setRefreshTrigger((prev) => prev + 1);
        setShowImportDropdown(false);
        if (count > 0) window.alert(p('import_success', { count }));
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('import_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        const rows = Array.isArray(data) ? data : [data];
        let count = 0;
        for (const item of rows) {
          if (await postImportedRow(item)) count += 1;
        }
        setRefreshTrigger((prev) => prev + 1);
        setShowImportDropdown(false);
        if (count > 0) window.alert(p('import_success', { count }));
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('import_json_error'));
      }
    }, '.json');
  };

  const openCreateModal = () => {
    if (!articlesConfig.enableCostMethods) {
      alert(p('cost_methods_disabled', { defaultValue: 'طرق التكلفة معطّلة من إعدادات الأصناف.' }));
      return;
    }
    setEditingRecord(null);
    setFormData({
      ...EMPTY_FORM,
      method_code: articlesConfig.autoGenerateCostMethodReference !== false ? generateMethodCode() : '',
    });
    setShowModal(true);
  };

  const methodsArray = Array.isArray(methods) ? methods : [];
  const uniqueTypes = new Set(methodsArray.map((m) => m.method_type).filter(Boolean));

  const stats = {
    total: methodsArray.length,
    actifs: countArticleActive(methodsArray),
    inactifs: countArticleInactive(methodsArray),
    defaults: methodsArray.filter((m) => m.is_default === 1 || m.is_default === true).length,
    types: uniqueTypes.size,
  };

  const mainTabs = [
    { id: 'all_methods', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_type', name: t('common.by_type'), icon: Calculator },
    { id: 'by_status', name: t('common.by_status'), icon: TrendingUp },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'actif', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactif', name: p('filter_inactive'), icon: X },
    { id: 'default', name: p('stat_default'), icon: BadgeCheck },
  ];

  const methodsDistribution = {
    all_methods: {
      title: p('dist_overview'),
      data: [
        { name: p('stat_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('stat_default'), count: stats.defaults, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
      ],
    },
    by_type: {
      title: t('common.by_type'),
      data: METHOD_TYPE_DEFS.map((typeDef) => ({
        name: p(typeDef.labelKey),
        count: methodsArray.filter((m) => m.method_type === typeDef.value).length,
        color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      })),
    },
    by_status: {
      title: t('common.by_status'),
      data: [
        { name: p('stat_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
  };

  const distributionInfo = methodsDistribution[mainViewMode] || methodsDistribution.all_methods;

  const baseFilteredMethods = useMemo(
    () =>
      methodsArray.filter((record) => {
        if (statusFilter !== 'all' && record.status !== statusFilter) return false;

        if (dateRange.start || dateRange.end) {
          const createdDate = record.created_at ? new Date(record.created_at) : null;
          if (createdDate) {
            if (dateRange.start) {
              const startOfDay = new Date(dateRange.start);
              startOfDay.setHours(0, 0, 0, 0);
              if (createdDate < startOfDay) return false;
            }
            if (dateRange.end) {
              const endOfDay = new Date(dateRange.end);
              endOfDay.setHours(23, 59, 59, 999);
              if (createdDate > endOfDay) return false;
            }
          }
        }

        const matchesSearch =
          !searchTerm ||
          record.method_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.method_type?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (!qawaem) {
          if (!matchesArticleStatusFilter(record.status, quickFilter)) return false;
          if (quickFilter === 'default' && !(record.is_default === 1 || record.is_default === true)) return false;
        }

        return true;
      }),
    [methodsArray, statusFilter, dateRange, searchTerm, qawaem, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('method_code', (r) => r.method_code || ''),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('method_type', (r) => getMethodTypeLabel(r.method_type)),
      textColumnRule('currency', (r) => fmtCurrency(r.currency)),
      textColumnRule('is_default', (r) => ((r.is_default === 1 || r.is_default === true) ? p('yes') : p('no'))),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t, p],
  );

  const filteredMethods = useMemo(
    () => applyColumnFilters(baseFilteredMethods, colFilters, columnFilterRules),
    [baseFilteredMethods, colFilters, columnFilterRules],
  );

  const filteredMethodIds = useMemo(() => filteredMethods.map((m) => m.id), [filteredMethods]);
  const displayedMethods = useMemo(
    () => sliceArticlesListItems(filteredMethods, articlesConfig.itemsPerPage),
    [filteredMethods, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredMethodIds.length > 0 && filteredMethodIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredMethodIds);
    }
  };

  const getExportData = useCallback(
    () =>
      selectedRecords.length > 0
        ? methodsArray.filter((m) => selectedRecords.includes(m.id))
        : filteredMethods,
    [methodsArray, selectedRecords, filteredMethods],
  );

  const handleExportCSV = useCallback(() => {
    exportToCSV(getExportData(), `cost_methods_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  }, [getExportData]);

  const handleExportExcel = useCallback(() => {
    exportToExcel(getExportData(), `cost_methods_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportDropdown(false);
  }, [getExportData]);

  const handleExportJSON = useCallback(() => {
    const blob = new Blob([JSON.stringify(getExportData(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cost_methods_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  }, [getExportData]);

  const importExportProps = useMemo(
    () =>
      buildArticlesImportExportProps({
        showImportDropdown,
        setShowImportDropdown,
        showExportDropdown,
        setShowExportDropdown,
        importDropdownRef,
        exportDropdownRef,
        p,
        t,
        handlers: {
          onImportCSV: handleImportCSV,
          onImportExcel: handleImportExcel,
          onImportJSON: handleImportJSON,
          onExportCSV: handleExportCSV,
          onExportExcel: handleExportExcel,
          onExportJSON: handleExportJSON,
        },
      }),
    [
      showImportDropdown,
      showExportDropdown,
      t,
      handleImportCSV,
      handleImportExcel,
      handleImportJSON,
      handleExportCSV,
      handleExportExcel,
      handleExportJSON,
    ],
  );

  const getStatusBadge = (status) => (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${getCatalogStatusBadgeStyle(status)}`}>
      {isArticleActiveStatus(status) ? <CheckCircle size={14} /> : <X size={14} />}
      {getArticleStatusLabel(status, t)}
    </span>
  );

  const renderMethodType = (type) => {
    const tone = {
      fifo: qawaem || isDashboardLightMode ? 'text-[#5a855a]' : 'text-blue-300',
      weighted_average: qawaem || isDashboardLightMode ? 'text-[#7a5a9b]' : 'text-purple-300',
      standard: qawaem || isDashboardLightMode ? 'text-[#a67c3d]' : 'text-amber-300',
      last_purchase: qawaem || isDashboardLightMode ? 'text-[#6e9b6e]' : 'text-cyan-300',
    };
    return (
      <span className={`text-sm font-medium ${tone[type] || (qawaem ? 'text-console-muted' : 'text-white/70')}`}>
        {getMethodTypeLabel(type)}
      </span>
    );
  };

  const renderDefaultMark = () => (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#6e9b6e]">
      <BadgeCheck size={15} aria-hidden />
      {p('stat_default')}
    </span>
  );

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Calculator, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.actifs, icon: CheckCircle, variant: 'forest' },
      { key: 'inactive', label: p('stat_inactive'), value: stats.inactifs, icon: X, variant: 'copper' },
      { key: 'default', label: p('stat_default'), value: stats.defaults, icon: BadgeCheck, variant: 'forest' },
    ],
    [stats, p],
  );

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: p('all_statuses') },
      ...buildArticleStatusSelectOptions(t).map((o) => ({ value: o.value, label: o.label })),
    ],
    [t, p],
  );

  const qawaemStatusLabel =
    qawaemStatusOptions.find((o) => o.value === statusFilter)?.label ?? p('all_statuses');

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'method_code', 'name', 'method_type', 'is_default', 'currency', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => ensureStatusBeforeActions([
      { key: 'method_code', label: p('col_code'), checked: columnVisibility.method_code !== false, required: true },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'method_type', label: p('col_type'), checked: columnVisibility.method_type !== false },
      { key: 'is_default', label: p('col_default'), checked: columnVisibility.is_default !== false },
      { key: 'currency', label: p('col_currency'), checked: columnVisibility.currency !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ]),
    [t, p, columnVisibility],
  );

  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mainViewMode === 'all_methods' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
          {distributionInfo.data.map((item, index) => (
            <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div
                  className={`h-3 rounded-full ${item.color}`}
                  style={{ width: `${stats.total > 0 ? (item.count / stats.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <ArticlesConfigNotice
        message={!articlesConfig.enableCostMethods ? p('notice_cost_methods_disabled', { defaultValue: 'طرق التكلفة معطّلة — فعّلها من إعدادات الأصناف ← التكلفة والأسعار.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Calculator}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildBulkDeleteFilterAction({
          count: selectedRecords.length,
          onClick: handleBulkDelete,
          label: p('delete_selected', { count: selectedRecords.length }),
          title: p('delete_selected', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={openCreateModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {shouldShowCardsToolbarSearch(viewMode) ? (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : null}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('method_code') ? (
                    <TableColumnHeaderFilter
                      columnKey="method_code"
                      label={p('col_code')}
                      value={colFilters.method_code ?? ''}
                      onChange={(v) => setColFilter('method_code', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('name') ? (
                    <TableColumnHeaderFilter
                      columnKey="name"
                      label={p('col_name')}
                      value={colFilters.name ?? ''}
                      onChange={(v) => setColFilter('name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('method_type') ? (
                    <TableColumnHeaderFilter
                      columnKey="method_type"
                      label={p('col_type')}
                      value={colFilters.method_type ?? ''}
                      onChange={(v) => setColFilter('method_type', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('is_default') ? (
                    <TableColumnHeaderFilter columnKey="is_default" label={p('col_default')} value={colFilters.is_default ?? ''} onChange={(v) => setColFilter('is_default', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" />
                  ) : null}
                  {showCol('currency') ? (
                    <TableColumnHeaderFilter
                      columnKey="currency"
                      label={p('col_currency')}
                      value={colFilters.currency ?? ''}
                      onChange={(v) => setColFilter('currency', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" />
                  ) : null}
                  <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">{t('common.loading')}</td>
                  </tr>
                ) : displayedMethods.length > 0 ? (
                  displayedMethods.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {showCol('method_code') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold font-mono">{record.method_code || p('empty_dash')}</span>
                            <span className="text-white/50 text-xs mt-0.5">{record.id}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4 text-white font-semibold">{record.name}</td>
                      ) : null}
                      {showCol('method_type') ? (
                        <td className="px-6 py-4">{renderMethodType(record.method_type)}</td>
                      ) : null}
                      {showCol('is_default') ? (
                        <td className="px-6 py-4 text-center">
                          {(record.is_default === 1 || record.is_default === true) ? (
                            renderDefaultMark()
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                      ) : null}
                      {showCol('currency') ? (
                        <td className="px-6 py-4 text-white">{fmtCurrency(record.currency)}</td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={STATUS_OPTIONS}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Calculator size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedMethods.length > 0 ? (
              displayedMethods.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Calculator size={20} className="text-blue-400" />
                          {record.name}
                        </h3>
                        {renderMethodType(record.method_type)}
                        {(record.is_default === 1 || record.is_default === true) && (
                          <span className="text-xs font-medium text-[#6e9b6e]">{p('stat_default')}</span>
                        )}
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_identification')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_code')}:</span> <span className="font-mono">{record.method_code || p('empty_dash')}</span></div>
                            <div><span className="font-medium">{p('col_name')}:</span> {record.name}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_method')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_type')}:</span> {renderMethodType(record.method_type)}</div>
                            {record.description && (
                              <div className="italic text-white/60">{record.description}</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_scope')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_currency')}:</span> {fmtCurrency(record.currency)}</div>
                            <div>
                              <span className="font-medium">{p('col_default')}:</span>{' '}
                              {(record.is_default === 1 || record.is_default === true) ? p('yes') : p('no')}
                            </div>
                            {record.notes && (
                              <div className="italic pt-1 border-t border-white/10">{record.notes}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Calculator size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Calculator className="h-6 w-6 text-blue-400" />
                {editingRecord ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="cost-methods-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? t('common.save') : t('common.create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="cost-methods-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_method_code')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <input
                      type="text"
                      value={formData.method_code}
                      onChange={(e) => setFormData({ ...formData, method_code: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={p('placeholder_method_code')}
                    />
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_name')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_name')} />
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_method_type')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <CustomSelect value={formData.method_type} onChange={(val) => setFormData({ ...formData, method_type: val })} options={methodTypeOptions} placeholder={p('placeholder_method_type')} searchable creatable autoSelect={false} />
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_status')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={statusOptions} placeholder={p('placeholder_status')} searchable={false} creatable={false} />
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_description')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_description')} />
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_currency')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    {currencyLocked ? (
                      <input type="text" readOnly value={`${SAUDI_CURRENCY_CODE} (${currencySuffix})`} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/80 font-mono" />
                    ) : (
                      <CustomSelect value={formData.currency} onChange={(val) => setFormData({ ...formData, currency: val })} options={currencyOptions} placeholder={p('placeholder_currency')} searchable creatable autoSelect={false} />
                    )}
                  </div>
                  <div className="min-w-0 flex items-end pb-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} className="w-5 h-5 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500" />
                      <span className="text-white font-medium flex items-center gap-2">
                        <BadgeCheck size={16} className="text-[#6e9b6e]" aria-hidden />
                        {p('field_is_default')}
                      </span>
                    </label>
                  </div>
                  <div className="min-w-0">
                    <ErpFieldLabel label={p('field_notes')} className="block w-full text-white/70 text-sm mb-2 font-medium text-right" />
                    <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_notes')} />
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body,
      )}

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4)', width: '95%', maxWidth: '900px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Calculator className="h-6 w-6 text-blue-400" />
                {viewingRecord.name}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Hash size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_identification')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase mb-1">{p('col_code')}</p>
                    <p className="text-white font-semibold font-mono">{viewingRecord.method_code || p('empty_dash')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase mb-1">{p('col_name')}</p>
                    <p className="text-white font-semibold">{viewingRecord.name}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase mb-1">{t('common.status')}</p>
                    {getStatusBadge(viewingRecord.status)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Calculator size={18} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_method')}</h3>
                  </div>
                  <div className="space-y-3 text-white/80">
                    <div>{renderMethodType(viewingRecord.method_type)}</div>
                    {viewingRecord.description && (
                      <p className="text-white/70 text-sm">{viewingRecord.description}</p>
                    )}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Coins size={18} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_scope')}</h3>
                  </div>
                  <div className="space-y-2 text-white/80">
                    <div>
                      <span className="text-white/50">{p('col_currency')}:</span>{' '}
                      {fmtCurrency(viewingRecord.currency)}
                    </div>
                    <div>
                      <span className="text-white/50">{p('col_default')}:</span>{' '}
                      {(viewingRecord.is_default === 1 || viewingRecord.is_default === true) ? p('yes') : p('no')}
                    </div>
                  </div>
                </div>
              </div>

              {viewingRecord.notes && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('field_notes')}</h3>
                  </div>
                  <p className="text-white/70 text-sm">{viewingRecord.notes}</p>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowViewModal(false)}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body,
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default CostMethodsPage;
